import { BookingStateEnum } from '@xeetr/types/enums'
import { BaseBookingModel, FoodBookingModel, ServiceBookingModel } from '@xeetr/types/models'
import { isFoodBookingModel, isServiceBookingModel } from '@xeetr/types/utils'
import { memo } from 'react'

import { DataTable } from '@/components/ui/data-table'
import EmptyState from '@/components/ui/empty-state'
import { useAbsences } from '@/hooks/useAbsences'
import { useBookings } from '@/hooks/useBookings'
import { useEvents } from '@/hooks/useEvents'
import { useSidebar } from '@/hooks/useSidebar'
import { useColumnSizing } from '@/hooks/useUserSettings'
import { useUserSettings } from '@/hooks/useUserSettings'
import { useTenant } from '@/hooks/useTenant'
import { cn } from '@/lib/utils'
import { SidebarType } from '@/types/Sidebar'
import { parseAbsenceBookingId, parseEventBookingId } from '@/utils/bookingIds'
import { useColumnOrder } from '@/hooks/useColumnOrder'
import { useDashboardColumns } from '@/hooks/dashboard/useDashboardColumns'
import { useRowStyle } from '@/hooks/dashboard/useRowStyle'
import { useColumnVisibility } from '@/hooks/dashboard/useColumnVisibility'
import { useOpenBookings } from '@/hooks/useOpenBookings'

const searchKeys = [
  'customer.firstName',
  'customer.lastName',
  'customer.companyName',
  'customer.phone',
  'customer.mail',
  'bookingAdditions.additionalInfo',
  'bookingAdditions.note',
  'bookingAdditions.customFields',
  'state.status',
  'state.reason',
  'state.comment',
  'room.name',
]

const Requests = memo(() => {
  const allColumns = useDashboardColumns()
  const { isLoading } = useBookings()
  const { absences } = useAbsences()
  const { events } = useEvents()
  const { setSidebar } = useSidebar()
  const { bookings } = useOpenBookings()

  const { tenant } = useTenant()
  const { settings, updateSettings } = useUserSettings()
  const mergeDateAndTime = tenant?.id
    ? (settings.dashboard?.[tenant.id]?.mergeDateAndTime ?? false)
    : false

  const toggleMergeDateAndTime = (value: boolean) => {
    if (!tenant?.id) return

    const dashboardSettings = settings.dashboard || {}
    updateSettings({
      dashboard: {
        ...dashboardSettings,
        [tenant.id]: {
          ...(dashboardSettings[tenant.id] || {}),
          mergeDateAndTime: value,
        },
      },
    })
  }

  // Column sizing persistence
  const { columnSizing, setColumnSizing } = useColumnSizing('bookings')

  const { handleRowStyle } = useRowStyle()

  const { orderedColumns, updateColumnOrder } = useColumnOrder('dashboard')
  const { initialColumnVisibility, handleColumnVisibilityChange } = useColumnVisibility(
    allColumns,
    'dashboard'
  )

  if (isLoading) {
    return (
      <div className="bg-card flex h-full items-center justify-center rounded-lg border p-8 shadow-sm">
        <div className="flex flex-col items-center gap-3">
          <div className="border-primary h-10 w-10 animate-spin rounded-full border-4 border-t-transparent" />
          <span className="text-muted-foreground text-sm">Reservierungen werden geladen...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4">
      <DataTable
        columns={allColumns}
        columnOrder={orderedColumns}
        onColumnOrderChange={updateColumnOrder}
        data={bookings}
        showColumnVisibility={true}
        showPagination={false}
        controlsLayout="toolbar"
        onColumnVisibilityChange={handleColumnVisibilityChange}
        initialColumnVisibility={initialColumnVisibility}
        enableColumnResizing={true}
        initialColumnSizing={columnSizing}
        columnSettingsPosition={'top-right'}
        columnVisibilityPosition={'top-right'}
        onColumnSizingChange={setColumnSizing}
        enableColumnReordering={true}
        showDateTimeToggle={true}
        dateTimeToggleLabel="Datum und Uhrzeit"
        dateTimeToggleValue={mergeDateAndTime}
        onDateTimeToggleChange={toggleMergeDateAndTime}
        alternateRowColors={true}
        searchPosition="top-left"
        searchKeys={searchKeys}
        emptyState={
          <EmptyState
            title="Keine Buchungen gefunden"
            description="Es gibt keine offene Buchungen."
          />
        }
        rowClassName={(booking) =>
          cn(handleRowStyle(booking), {
            'cursor-pointer': booking.state?.status !== BookingStateEnum.HOLIDAY,
          })
        }
        onRowClick={(row: BaseBookingModel) => {
          const bookingState = row.state?.status
          if (
            (isFoodBookingModel(row) || isServiceBookingModel(row)) &&
            bookingState !== BookingStateEnum.ABSENCE &&
            bookingState !== BookingStateEnum.EVENT
          ) {
            setSidebar({
              type: SidebarType.BOOKING_EDIT,
              open: true,
              booking: row as FoodBookingModel | ServiceBookingModel,
            })
          } else if (bookingState === BookingStateEnum.ABSENCE) {
            const parsed = parseAbsenceBookingId(row.id)
            if (parsed) {
              const absence = absences.find((a) => a.id === parsed.absenceId)
              if (absence) {
                setSidebar({
                  type: SidebarType.ABSENCE_EDIT,
                  open: true,
                  absence: absence,
                })
              }
            }
          } else if (bookingState === BookingStateEnum.EVENT) {
            const parsed = parseEventBookingId(row.id)
            if (parsed) {
              const event = events.find((e) => e.id === parsed.eventId)
              if (event) {
                setSidebar({
                  type: SidebarType.EVENT_EDIT,
                  open: true,
                  event: event,
                })
              }
            }
          }
        }}
      />
    </div>
  )
})

Requests.displayName = 'Requests'

export default Requests
